import { createFileRoute } from '@tanstack/react-router';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Card, CardContent } from '@caratuva/ui';
import { Section, SectionHeading } from '../components/Section.tsx';
import { useSalesContact } from '../components/SalesContact.tsx';
import { GETPAID_URL, SIGNUP_URL } from '../lib/links.ts';
import { useRouteMeta } from '../lib/useDocumentMeta.ts';

export const Route = createFileRoute('/pricing')({
  component: PricingPage,
});

function PlanCard({
  titleId,
  amountId,
  unitId,
  bodyId,
  highlight,
}: {
  titleId: string;
  amountId: string;
  unitId?: string;
  bodyId: string;
  highlight?: boolean;
}) {
  return (
    <Card className={highlight ? 'border-[var(--color-navy-300)]' : undefined}>
      <CardContent className="pt-6">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-[var(--color-navy-600)]">
          <FormattedMessage id={titleId} />
        </h3>
        <p className="mt-3 text-3xl font-semibold text-[var(--color-navy-950)]">
          <FormattedMessage id={amountId} />
        </p>
        {unitId ? (
          <p className="mt-1 text-sm text-[var(--color-navy-500)]">
            <FormattedMessage id={unitId} />
          </p>
        ) : null}
        <p className="mt-4 text-sm text-[var(--color-navy-700)]">
          <FormattedMessage id={bodyId} />
        </p>
      </CardContent>
    </Card>
  );
}

function ComparisonTable() {
  const rows = [
    {
      label: 'pricing.compare.row.speed',
      c: 'pricing.compare.speed.caratuva',
      b: 'pricing.compare.speed.bank',
    },
    {
      label: 'pricing.compare.row.cost',
      c: 'pricing.compare.cost.caratuva',
      b: 'pricing.compare.cost.bank',
    },
    {
      label: 'pricing.compare.row.cambio',
      c: 'pricing.compare.cambio.caratuva',
      b: 'pricing.compare.cambio.bank',
    },
    {
      label: 'pricing.compare.row.payout',
      c: 'pricing.compare.payout.caratuva',
      b: 'pricing.compare.payout.bank',
    },
  ];
  return (
    <div className="mx-auto mt-12 max-w-3xl overflow-x-auto">
      <table className="w-full border-collapse text-left text-sm">
        <thead>
          <tr className="border-b border-[var(--color-beige-300)]">
            <th className="py-3 pr-4 font-medium text-[var(--color-navy-500)]">
              <FormattedMessage id="pricing.compare.col.feature" />
            </th>
            <th className="py-3 pr-4 font-semibold text-[var(--color-navy-950)]">
              <FormattedMessage id="pricing.compare.col.caratuva" />
            </th>
            <th className="py-3 font-medium text-[var(--color-navy-700)]">
              <FormattedMessage id="pricing.compare.col.bank" />
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.label} className="border-b border-[var(--color-beige-200)]">
              <td className="py-3 pr-4 font-medium text-[var(--color-navy-800)]">
                <FormattedMessage id={r.label} />
              </td>
              <td className="py-3 pr-4 text-[var(--color-navy-950)]">
                <FormattedMessage id={r.c} />
              </td>
              <td className="py-3 text-[var(--color-navy-600)]">
                <FormattedMessage id={r.b} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TierTable() {
  const rows = [
    { volume: 'pricing.tiers.row1.volume', fee: 'pricing.tiers.row1.fee' },
    { volume: 'pricing.tiers.row2.volume', fee: 'pricing.tiers.row2.fee' },
    { volume: 'pricing.tiers.row3.volume', fee: 'pricing.tiers.row3.fee' },
    { volume: 'pricing.tiers.row4.volume', fee: 'pricing.tiers.row4.fee' },
  ];
  return (
    <div className="mx-auto mt-6 max-w-xl overflow-x-auto">
      <table className="w-full border-collapse text-left text-sm">
        <thead>
          <tr className="border-b border-[var(--color-beige-300)]">
            <th className="py-3 pr-4 font-medium text-[var(--color-navy-500)]">
              <FormattedMessage id="pricing.tiers.col.volume" />
            </th>
            <th className="py-3 text-right font-semibold text-[var(--color-navy-950)]">
              <FormattedMessage id="pricing.tiers.col.fee" />
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.volume} className="border-b border-[var(--color-beige-200)]">
              <td className="py-3 pr-4 font-medium text-[var(--color-navy-800)]">
                <FormattedMessage id={r.volume} />
              </td>
              <td className="py-3 text-right font-semibold text-[var(--color-navy-950)]">
                <FormattedMessage id={r.fee} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// "Get paid" — sellers. The per-transaction fee, transparent and self-serve.
function SellerPricing() {
  const intl = useIntl();
  return (
    <Section tint="white" className="pt-20 sm:pt-24">
      <SectionHeading
        eyebrow={<FormattedMessage id="pricing.eyebrow" />}
        title={<FormattedMessage id="pricing.title" />}
        subtitle={<FormattedMessage id="pricing.subtitle" />}
      />
      <div className="mx-auto mt-12 max-w-2xl">
        <SectionHeading title={<FormattedMessage id="pricing.sellers.title" />} />
      </div>
      <div className="mx-auto mt-8 grid max-w-4xl gap-6 sm:grid-cols-2">
        <PlanCard
          titleId="pricing.fee.title"
          amountId="pricing.fee.amount"
          unitId="pricing.fee.unit"
          bodyId="pricing.fee.body"
          highlight
        />
        <PlanCard
          titleId="pricing.getpaid.title"
          amountId="pricing.getpaid.amount"
          bodyId="pricing.getpaid.body"
        />
      </div>
      <p className="mx-auto mt-6 max-w-2xl text-center text-sm text-[var(--color-navy-500)]">
        <FormattedMessage id="pricing.sellers.note" />
      </p>
      <p className="mx-auto mt-3 max-w-2xl text-center text-sm font-medium text-[var(--color-navy-800)]">
        <FormattedMessage id="pricing.sellers.referralDiscount" />
      </p>
      <div className="mt-8 flex justify-center">
        <Button asChild size="lg">
          <a href={GETPAID_URL}>{intl.formatMessage({ id: 'pricing.sellers.cta' })}</a>
        </Button>
      </div>
    </Section>
  );
}

// "Platforms & integrators" — the volume tiers, monthly fee, and sandbox.
function IntegratorPricing() {
  const intl = useIntl();
  const { open: openSalesContact } = useSalesContact();
  return (
    <Section tint="canvas">
      <SectionHeading
        eyebrow={<FormattedMessage id="pricing.integrators.eyebrow" />}
        title={<FormattedMessage id="pricing.integrators.title" />}
        subtitle={<FormattedMessage id="pricing.integrators.subtitle" />}
      />
      <div className="mx-auto mt-12 grid max-w-4xl gap-6 sm:grid-cols-3">
        <PlanCard
          titleId="pricing.fee.title"
          amountId="pricing.fee.amount"
          unitId="pricing.fee.unit"
          bodyId="pricing.fee.body"
          highlight
        />
        <PlanCard
          titleId="pricing.monthly.title"
          amountId="pricing.monthly.amount"
          bodyId="pricing.monthly.body"
        />
        <PlanCard
          titleId="pricing.sandbox.title"
          amountId="pricing.sandbox.amount"
          bodyId="pricing.sandbox.body"
        />
      </div>
      <div className="mx-auto mt-12 max-w-xl text-center">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-[var(--color-navy-600)]">
          <FormattedMessage id="pricing.tiers.title" />
        </h3>
      </div>
      <TierTable />
      <p className="mx-auto mt-3 max-w-xl text-center text-xs text-[var(--color-navy-500)]">
        <FormattedMessage id="pricing.tiers.note" />
      </p>
      <p className="mx-auto mt-6 max-w-2xl text-center text-sm text-[var(--color-navy-500)]">
        <FormattedMessage id="pricing.note" />
      </p>
      <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
        <Button asChild size="lg">
          <a href={SIGNUP_URL}>{intl.formatMessage({ id: 'pricing.integrators.cta.primary' })}</a>
        </Button>
        <Button type="button" size="lg" variant="outline" onClick={openSalesContact}>
          {intl.formatMessage({ id: 'pricing.integrators.cta.secondary' })}
        </Button>
      </div>
    </Section>
  );
}

function PricingPage() {
  useRouteMeta('meta.pricing.title', 'meta.pricing.description', 'meta.pricing.social');
  const intl = useIntl();
  return (
    <>
      <SellerPricing />
      <IntegratorPricing />

      <Section tint="white">
        <SectionHeading title={<FormattedMessage id="pricing.compare.title" />} />
        <ComparisonTable />
      </Section>

      <Section tint="brand">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-semibold tracking-tight text-[var(--color-beige-50)] sm:text-4xl">
            <FormattedMessage id="pricing.cta.title" />
          </h2>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button asChild size="lg">
              <a href={GETPAID_URL}>{intl.formatMessage({ id: 'pricing.cta.primary' })}</a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-[rgba(251,248,239,0.5)] bg-[rgba(251,248,239,0.1)] backdrop-blur-sm text-[var(--color-beige-50)] hover:bg-[rgba(251,248,239,0.2)] hover:text-[var(--color-beige-50)]"
            >
              <a href={SIGNUP_URL}>{intl.formatMessage({ id: 'pricing.cta.secondary' })}</a>
            </Button>
          </div>
        </div>
      </Section>
    </>
  );
}
